import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_DELIVERY_QUOTE,
} from '../../../Constants/Config';

const normalizeQuoteText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const normalizeQuoteCoordinate = (value) => {
  const coordinate = Number.parseFloat(value);
  return Number.isFinite(coordinate) ? coordinate.toFixed(6) : '';
};

export const createDeliveryQuoteProvenance = (payload = {}) => {
  const merchantId = String(payload?.merchant_id ?? '').trim();
  const manifestTotalValue = Number.parseFloat(payload?.manifest_total_value);
  const manifestTotalCents = Number.isFinite(manifestTotalValue)
    ? Math.max(0, Math.round(manifestTotalValue))
    : 0;
  const addressParts = [
    normalizeQuoteText(payload?.cus_del_address),
    normalizeQuoteText(payload?.cus_del_address_2),
    normalizeQuoteText(payload?.cus_del_city),
    normalizeQuoteText(payload?.cus_del_state),
    normalizeQuoteText(payload?.cus_del_zip),
    normalizeQuoteCoordinate(payload?.dropoff_latitude),
    normalizeQuoteCoordinate(payload?.dropoff_longitude),
  ];
  const addressKey = JSON.stringify(addressParts);

  return {
    key: JSON.stringify([merchantId, addressKey, manifestTotalCents]),
    merchantId,
    addressKey,
    manifestTotalCents,
  };
};

export const isDeliveryQuoteProvenanceCurrent = ({
  deliveryQuoteProvenance,
  selectedDeliveryQuoteProvenance,
  deliveryQuoteData,
  grandTotal,
  deliveryFee,
}) => {
  const parsedGrandTotal = Number(grandTotal);
  const parsedDeliveryFee = Number(deliveryFee);
  const parsedQuoteFee = Number(deliveryQuoteData?.fee);
  if (
    !Number.isFinite(parsedGrandTotal) ||
    parsedGrandTotal < 0 ||
    !Number.isFinite(parsedDeliveryFee) ||
    parsedDeliveryFee < 0 ||
    !Number.isFinite(parsedQuoteFee) ||
    parsedQuoteFee < 0
  ) {
    return false;
  }

  const manifestTotalCents = Math.round(
    Math.max(0, parsedGrandTotal - parsedDeliveryFee) * 100,
  );
  const deliveryFeeCents = Math.round(parsedDeliveryFee * 100);
  const quoteFeeCents = Math.round(parsedQuoteFee * 100);
  const quoteId = String(deliveryQuoteData?.quote_id || '');

  return (
    deliveryQuoteProvenance?.status === 'fulfilled' &&
    selectedDeliveryQuoteProvenance?.status === 'fulfilled' &&
    Boolean(deliveryQuoteProvenance?.key) &&
    deliveryQuoteProvenance.key === selectedDeliveryQuoteProvenance.key &&
    Boolean(deliveryQuoteProvenance?.requestId) &&
    deliveryQuoteProvenance.requestId ===
      selectedDeliveryQuoteProvenance.requestId &&
    Boolean(quoteId) &&
    String(deliveryQuoteProvenance?.quoteId || '') === quoteId &&
    deliveryFeeCents === quoteFeeCents &&
    Number(deliveryQuoteProvenance?.manifestTotalCents) ===
      manifestTotalCents &&
    Number(selectedDeliveryQuoteProvenance?.manifestTotalCents) ===
      manifestTotalCents
  );
};

// Thunk to fetch delivery quote (e.g. Uber delivery fee)
export const fetchDeliveryQuote = createAsyncThunk(
  'deliveryAddress/fetchDeliveryQuote',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL_SANDBOX + GET_DELIVERY_QUOTE,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (response?.data?.success === true) {
        return response.data;
      }

      return rejectWithValue(
        response.data || { message: 'Failed to get delivery quote' },
      );
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: error.message || 'Delivery quote error',
        },
      );
    }
  },
);

// Define initial state
const initialState = {
  addresses: [],
  // Delivery quote state
  deliveryQuoteData: {},
  deliveryQuoteLoading: false,
  deliveryQuoteError: null,
  deliveryQuoteProvenance: null,
  activeDeliveryQuoteRequestId: null,
};

// Create slice
const deliveryAddressSlice = createSlice({
  name: 'deliveryAddress',
  initialState,
  reducers: {
    // Define reducer functions here
    addAddress(state, action) {
      // If the payload is an empty array, clear the addresses
      if (Array.isArray(action.payload) && action.payload.length === 0) {
        state.addresses = [];
      } else {
        // Otherwise, add the new address(es) to the existing addresses array
        state.addresses.push(...action.payload);
      }
    },
    clearDeliveryQuoteData(state) {
      state.deliveryQuoteData = {};
      state.deliveryQuoteLoading = false;
      state.deliveryQuoteError = null;
      state.deliveryQuoteProvenance = null;
      state.activeDeliveryQuoteRequestId = null;
    },
    // You can define other reducer functions here
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeliveryQuote.pending, (state, action) => {
        state.deliveryQuoteLoading = true;
        state.deliveryQuoteError = null;
        state.deliveryQuoteData = {};
        state.activeDeliveryQuoteRequestId = action.meta.requestId;
        state.deliveryQuoteProvenance = {
          ...createDeliveryQuoteProvenance(action.meta.arg),
          requestId: action.meta.requestId,
          status: 'pending',
        };
      })
      .addCase(fetchDeliveryQuote.fulfilled, (state, action) => {
        if (state.activeDeliveryQuoteRequestId !== action.meta.requestId) {
          return;
        }

        state.deliveryQuoteLoading = false;
        state.deliveryQuoteData = action.payload || {};
        state.deliveryQuoteProvenance = {
          ...createDeliveryQuoteProvenance(action.meta.arg),
          requestId: action.meta.requestId,
          status: 'fulfilled',
          quoteId: action.payload?.quote_id ?? '',
        };
        state.activeDeliveryQuoteRequestId = null;
      })
      .addCase(fetchDeliveryQuote.rejected, (state, action) => {
        if (state.activeDeliveryQuoteRequestId !== action.meta.requestId) {
          return;
        }

        state.deliveryQuoteLoading = false;
        state.deliveryQuoteData = {};
        state.deliveryQuoteError = action.payload || action.error || null;
        state.deliveryQuoteProvenance = {
          ...createDeliveryQuoteProvenance(action.meta.arg),
          requestId: action.meta.requestId,
          status: 'rejected',
        };
        state.activeDeliveryQuoteRequestId = null;
      });
  },
});

// Export actions and reducer
export const { addAddress, clearDeliveryQuoteData } =
  deliveryAddressSlice.actions;
export default deliveryAddressSlice.reducer;
